import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewEncapsulation,
  computed,
  inject,
  input,
  isDevMode,
  linkedSignal,
  signal,
} from '@angular/core';
import { APP_WINDOW, CDN_ORIGIN } from '@bhindle/shared/core';
import { AnimatedEmojisList } from '../../data/animated-emojis.data';
import { getEmojiCode } from '../../helpers';
import { EMOJI_PATH } from '../../tokens';

@Component({
  selector: 'bd-emoji',
  imports: [],
  template: `
    @if (showAnimation() && isAnimatedEmojiAvailable()) {
      <picture>
        <!-- Capped at @2x, deliberately.
             The srcset used to run to @4x, and a modern phone reports DPR 3 or
             4 — so on the surfaces where this component earns its place (a
             child's routine, a rewards grid, an empty state) the browser picked
             the largest cut for every emoji on screen. Measured on the
             marketing site, which made the same cut for the same reason, the
             @3x/@4x animated WebPs put roughly 1.5MB of *decoration* above the
             fold on a phone.
             An animated emoji is drawn at 24-48px on those surfaces; the @2x
             cut already covers that at DPR 2 and downscales cleanly above it,
             and the difference is invisible at this size against a payload that
             is not.

             detail="hero" opts out, because that premise is false for one
             consumer: bd-empty-content's default illustration is --size-12,
             160 CSS px, where the @2x cut (128x128) is *upscaled* even at
             DPR 1. The consumer declares that it renders large rather than
             passing a pixel count, because the size is set in CSS with a
             --empty-emoji-size escape hatch, and a number here would be a copy
             of that contract which nothing keeps in step.
             (No backticks in this comment: it lives inside the component's
             template literal, where one would close it.) -->
        <source
          [attr.srcset]="srcset()"
          type="image/webp"
        />
        <img
          [style.display]="isLoading() || isLoaded() ? 'block' : 'none'"
          [src]="basePath + emojiCode() + '.gif'"
          [alt]="emoji()"
          [width]="size()"
          [height]="size()"
          (error)="isLoaded.set(false); isLoading.set(false)"
          (load)="isLoaded.set(true)"
          fetchpriority="low"
          decoding="async"
          loading="lazy"
        />
      </picture>
    }
  `,
  styleUrl: './emoji.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.animated]': 'showAnimation() && isLoaded()',
    '[style.--_icon-size]': 'size() ? size() + "px" : "var(--icon-size)"',
    '[style.background-image]':
      'isFallbackVisible() || !isLoaded() ? svgPath() : undefined',
  },
})
export class EmojiComponent implements OnDestroy {
  #filePath: string = inject(EMOJI_PATH);
  #cdnOrigin: string = inject(CDN_ORIGIN);
  #window: Window = inject(APP_WINDOW);
  #visibilityChangeHandler: EventListener | null = null;
  #scrolling = false;
  #scrollingTimeout: ReturnType<typeof setTimeout> | undefined;
  #isVisible = false;
  #scrollContainer: HTMLElement | null = null;

  public emoji = input.required<string>();
  public showAnimation = input<boolean>(true);
  public size = input<number | undefined>();
  /**
   * How large this emoji is painted, which decides how far the cut ladder runs.
   * `compact` (24-48px: routine rows, chips, recents) stops at @2x to keep the
   * animated payload down; `hero` (an empty state's illustration) continues to
   * @4x so a 160px box is not handed a 128px bitmap.
   */
  public detail = input<'compact' | 'hero'>('compact');
  protected readonly isDevMode = isDevMode;
  protected readonly emojiCode = computed(() => getEmojiCode(this.emoji()));
  protected readonly srcset = computed(() => {
    const base = `${this.basePath}${this.emojiCode()}`;
    const cuts = this.detail() === 'hero' ? [2, 3, 4] : [2];
    return [`${base}.webp`, ...cuts.map((n) => `${base}@${n}x.webp ${n}x`)].join(', ');
  });
  protected readonly isCanvasSupported = signal(true);
  protected readonly isLoading = linkedSignal({
    source: this.emoji,
    computation: (src, prev) => !!src && src !== prev?.value,
  });
  protected readonly isLoaded = linkedSignal({
    source: this.emoji,
    computation: (src, prev) => !!src && src === prev?.value,
  });
  protected readonly isPaused = signal(false);
  protected readonly basePath = `${this.#cdnOrigin}${this.#filePath}`;
  protected readonly svgPath = computed(
    () => `url("${this.basePath}${this.emojiCode()}.svg")`,
  );
  protected readonly isFallbackVisible = computed(
    () =>
      (!this.isCanvasSupported() ||
        !this.isLoaded() ||
        !this.showAnimation() ||
        !this.isAnimatedEmojiAvailable()) &&
      !!this.svgPath(),
  );
  protected readonly isAnimatedEmojiAvailable = computed(() =>
    this.#isAnimatedEmojiAvailable(),
  );

  public ngOnDestroy() {
    this.#scrollContainer?.removeEventListener('scroll', this.#onScroll);
    if (this.#visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.#visibilityChangeHandler,
      );
    }
  }

  public pause() {
    this.isPaused.set(true);
  }

  public play() {
    this.isPaused.set(false);
  }

  #onScroll = () => {
    if (this.#scrolling) return;
    this.#scrolling = true;

    clearTimeout(this.#scrollingTimeout);
    this.#scrollingTimeout = setTimeout(() => {
      this.#scrolling = false;
      if (this.#isVisible && !this.isPaused()) {
      }
    }, 200);
  };

  #isAnimatedEmojiAvailable(): boolean {
    const emojiCode = this.emojiCode();
    return emojiCode ? AnimatedEmojisList.includes(this.emojiCode()) : false;
  }
}
